import { orderBy, find, flatten, uniqBy, uniq, cloneDeep, map, some, filter, findIndex, unionBy, sortBy, isEqual } from 'lodash';

import { SortType } from '../components/ColumnHeader';
import { utrTypeIds, acceptanceRankingValues, drawPlacementStatusTypeIds, drawCategoryTypeIds, eventFormatTypeIds, drawPositionTypeIds, drawEliminationTypeIds } from '../utils/eventConstants';
import { getAgeByBirthDate, getPickleBallRating, getRatingDetails, newGuid, setRatingChoice } from '../utils';

import {
    DRAW_EDIT_REINIT,
    DRAW_EDIT_GET_EVENT_SUCCESS,
    DRAW_EDIT_GET_EVENT_FAILURE,
    DRAW_EDIT_GET_CLUB_SUCCESS,
    DRAW_EDIT_GET_CLUB_FAILURE,
    DRAW_EDIT_GET_CLUB_OFFICIAL_MEMBERS_SUCCESS,
    DRAW_EDIT_GET_CLUB_OFFICIAL_MEMBERS_FAILURE,
    DRAW_EDIT_GET_DRAWS_SUCCESS,
    DRAW_EDIT_GET_PLAYERS_SUCCESS,
    DRAW_EDIT_GET_TEAMS_SUCCESS,
    DRAW_EDIT_GET_ACCEPTANCE_LIST,
    DRAW_EDIT_GET_ACCEPTANCE_LIST_SUCCESS,
    DRAW_EDIT_GET_ACCEPTANCE_LIST_FAILURE,
    DRAW_EDIT_GET_ORGANIZER_RATINGS_SUCCESS,
    DRAW_EDIT_GET_DRAW_SUCCESS,
    DRAW_EDIT_ADD_TO_DRAW,
    DRAW_EDIT_REMOVE_FROM_DRAW,
    DRAW_EDIT_ADD_TO_MATCH,
    DRAW_EDIT_CREATE_TEAM,
    DRAW_EDIT_ADD_TEAM_TO_EVENT,
    DRAW_EDIT_SPLIT_TEAMS,
    DRAW_EDIT_SET_SELECTED,
    DRAW_EDIT_SET_SELECTED_ND,
    DRAW_EDIT_SET_SELECTED_TEAMS,
    DRAW_EDIT_SET_SELECTED_TEAMS_ND,
    DRAW_EDIT_SET_PREVENT_SELECT_MOVE,
    DRAW_EDIT_ORDER_PLAYERS,
    DRAW_EDIT_SET_PLAYER_STATUS,
    DRAW_EDIT_SET_DRAW_TAG_SUCCESS,
    DRAW_EDIT_GET_MATCH_SUCCESS,
    DRAW_EDIT_UPDATE_SCHEDULE_TYPE,
    DRAW_EDIT_SAVE_SUCCESS_HIDE,
    DRAW_EDIT_SET_RESET_TIMER,
    DRAW_EDIT_SET_SAVING_FLAG,
    DRAW_EDIT_GET_PLAYERS_FAILURE,
    // ------------------------------------
    DRAW_EDIT_LOCATIONS_FETCH_REQUEST,
    DRAW_EDIT_LOCATIONS_FETCH_FAILURE,
    DRAW_EDIT_LOCATIONS_FETCH_SUCCESS,
    DRAW_EDIT_FACILITIES_FETCH_REQUEST,
    DRAW_EDIT_FACILITIES_FETCH_FAILURE,
    DRAW_EDIT_FACILITIES_FETCH_SUCCESS,
    DRAW_EDIT_LOCATIONS_EXCLUDE_REQUEST,
    DRAW_EDIT_LOCATIONS_EXCLUDE_SUCCESS,
    DRAW_EDIT_LOCATIONS_EXCLUDE_FAILURE,
    DRAW_EDIT_OFFICIALS_FETCH_REQUEST,
    DRAW_EDIT_OFFICIALS_FETCH_SUCCESS,
    DRAW_EDIT_OFFICIALS_FETCH_FAILURE,
    DRAW_EDIT_GET_DRAW_OFFICIALS_SUCCESS,
    DRAW_EDIT_FETCH_MATCH_SHEET_SUCCESS,
    DRAW_EDIT_DRAW_CONTACTS_SUCCESS,
    DRAW_EDIT_EDIT_TEAM_STANDING_SUCCESS,
    DRAW_EDIT_DELETE_TEAM_STANDING_SUCCESS,
    DRAW_EDIT_EVENT_TEAM_STANDINGS_SUCCESS,
    DRAW_EDIT_ADD_TEAM_STANDING_SUCCESS,
    DRAW_EDIT_UPDATE_MATCH_SHEET_SUCCESS,
    DRAW_EDIT_UPDATE_MATCH_SHEET_ROUND_SUCCESS,
    DRAW_EDIT_DELETE_MATCH_SHEET_SUCCESS,
    DRAW_EDIT_GET_ORDER_OF_PLAY_SUCCESS,
    DRAW_EDIT_SAVE_ORDER_OF_PLAY_SUCCESS,
    DRAW_EDIT_DELETE_ORDER_OF_PLAY_SUCCESS,
    DRAW_EDIT_GET_ORDER_OF_PLAY_MATCHES_SUCCESS,
    DRAW_EDIT_GET_MATCH_OFFICIALS_SUCCESS,
    DRAW_EDIT_UPDATE_DRAWS,
    DRAW_EDIT_SAVE,
    DRAW_EDIT_SAVE_SUCCESS,
    DRAW_EDIT_SAVE_FAILURE,
    DRAW_EDIT_PUBLISH,
    DRAW_EDIT_PUBLISH_SUCCESS,
    DRAW_EDIT_PUBLISH_FAILURE,
    DRAW_EDIT_SET_MOVE_FAILED_PLAYERS,
    DRAW_EDIT_GET_DRAW_DIVISION_ASSOCIATION_SUCCESS,
    DRAW_EDIT_UPDATE_CHECK_IN_STATUS_SUCCESS,
    DRAW_EDIT_CONFIRM_DIVISION_DRAW_ASSOCIATION_SUCCESS,
    DRAW_EDIT_GET_DRAWS_PUBLISH_INFO_SUCCESS,
    DRAW_EDIT_TOGGLE_SMS_FLAG,
} from '../actions/actionTypes/draw-edit-action-types';
import { getOtherTeamsOfTeamMembers, isMemberPlaced, isTeamPlaced } from '../utils/draws';
import { getOrganizerRating } from '../utils/events';
import { comparator } from '../utils/comparator';
import { getComparatorArguments } from '../utils/sorting';
import { gameTypes, sortParamTypes } from '../utils/constants';
import { SAVED_LISTS_PLAYER_MARKED_SAVED, SAVED_LISTS_PLAYER_MARKED_UNSAVED } from '../actions/actionTypes/saved-lists-action-types';

const _ = { orderBy, find, flatten, uniqBy, uniq, cloneDeep, map, some, filter, findIndex, unionBy };

// The organizer all-players list holds its own copy of each player, so the save/remove marker (shared
// across surfaces) must flip isSaved here too, or a Provider-view bookmark keeps its stale state.
// Returns the original players slice when the player isn't present so connected rows can bail.
const markDrawEditPlayerSaved = (players, playerId, isSaved) => {
    if (!Array.isArray(players?.data) || !players.data.some(player => player.playerId === playerId)) {
        return players;
    }
    const data = players.data.map(player => (player.playerId === playerId ? { ...player, isSaved } : player));
    const memberId = data.find(player => player.playerId === playerId)?.memberId;
    const playersDict = players.playersDict?.[playerId]
        ? { ...players.playersDict, [playerId]: { ...players.playersDict[playerId], isSaved } }
        : players.playersDict;
    const playersDictByMemberId = (memberId != null && players.playersDictByMemberId?.[memberId])
        ? { ...players.playersDictByMemberId, [memberId]: { ...players.playersDictByMemberId[memberId], isSaved } }
        : players.playersDictByMemberId;
    return { ...players, data, playersDict, playersDictByMemberId };
};

const initialState = {
    event: {
        loading: false,
        error: null,
        data: null,
    },
    club: {
        loading: false,
        error: null,
        data: null,
    },
    players: {
        loading: false,
        error: null,
        data: null,
        playersDict: {},
        playersDictByMemberId: {},
        membersInDraw: {},
        membersInWaitlist: {},
    },
    teams: {
        loading: false,
        error: null,
        data: null,
        teamsDict: {},
        teamsInDraw: {},
        membersInDraw: {},
    },
    draws: {
        loading: false,
        error: null,
        data: null,
        drawsDict: {},
    },
    parentDraw: {
        loading: false,
        error: null,
        data: null,
    },
    currentDraw: {
        loading: false,
        error: null,
        data: null,
        unsavedChanges: false,
    },
    locations: {
        loading: false,
        error: null,
        data: null,
    },
    officials: {
        loading: false,
        error: null,
        data: null,
    },
    drawOfficials: {
        loading: false,
        error: null,
        data: null,
    },
    matchSheet: {
        loading: false,
        error: null,
        data: [],
    },
    orderOfPlay: {
        loading: false,
        error: null,
        data: null,
    },
    acceptance: {},
    contacts: null,
    teamStandings: null,
    isSaving: false,
    isPublishing: false,
    savingError: false,
    organizerRatings: null,
    selected: [],
    selectedNotInDraw: [],
    selectedTeams: [],
    selectedTeamsNotInDraw: [],
    preventSelectMove: false,
    resetTimer: false,
    clubOfficialMembers: {
        loading: false,
        error: null,
        data: [],
    },
    failedMemberIds: [],
    drawDivision: null,
    associatedDivisionIds: [],
    associatedDivisionsByDrawId: {},
    drawsForPublish: [],
};

export default (state = initialState, action) => {
    switch (action.type) {
        case DRAW_EDIT_REINIT: {
            return initialState;
        }
        case SAVED_LISTS_PLAYER_MARKED_SAVED: {
            const players = markDrawEditPlayerSaved(state.players, action.playerId, true);
            if (players === state.players) {
                return state;
            }
            return { ...state, players };
        }
        case SAVED_LISTS_PLAYER_MARKED_UNSAVED: {
            const players = markDrawEditPlayerSaved(state.players, action.playerId, false);
            if (players === state.players) {
                return state;
            }
            return { ...state, players };
        }
        case DRAW_EDIT_GET_EVENT_SUCCESS: {
            const divisionsDict = {};
            if (action.payload?.divisions) {
                action.payload.divisions.forEach(div => {
                    divisionsDict[div.id] = div;
                });
            }
            return {
                ...state,
                event: {
                    loading: false,
                    error: null,
                    data: action.payload,
                    divisionsDict,
                },
            };
        }
        case DRAW_EDIT_GET_EVENT_FAILURE: {
            return {
                ...state,
                event: {
                    loading: false,
                    error: true,
                    data: null,
                },
            };
        }
        case DRAW_EDIT_GET_CLUB_SUCCESS: {
            return {
                ...state,
                club: {
                    loading: false,
                    error: null,
                    data: action.payload,
                },
            };
        }
        case DRAW_EDIT_GET_CLUB_FAILURE: {
            return {
                ...state,
                club: {
                    loading: false,
                    error: true,
                    data: null,
                },
            };
        }
        case DRAW_EDIT_GET_CLUB_OFFICIAL_MEMBERS_SUCCESS: {
            return {
                ...state,
                clubOfficialMembers: {
                    loading: false,
                    error: null,
                    data: action.payload,
                },
            };
        }
        case DRAW_EDIT_GET_CLUB_OFFICIAL_MEMBERS_FAILURE: {
            return {
                ...state,
                clubOfficialMembers: {
                    loading: false,
                    error: 'Failed to load club official members.',
                    data: [],
                },
            };
        }
        case DRAW_EDIT_GET_DRAWS_SUCCESS: {
            const { draws } = action.payload;
            const drawsDict = {};
            draws.forEach(draw => {
                drawsDict[draw.id] = draw;
                draw.childDraws.forEach(cd => {
                    drawsDict[cd.id] = {
                        ...cd,
                        isChildDraw: true,
                        parentDrawId: draw.id,
                    };
                });
            });
            const stateUpdate = {
                ...state,
                draws: {
                    loading: false,
                    error: null,
                    data: sortBy(draws, 'name'),
                    drawsDict,
                },
            };
            if (draws.length === 0) {
                stateUpdate.currentDraw = {
                    loading: false,
                    error: null,
                    data: null,
                    unsavedChanges: false,
                };
            }

            return {
                ...state,
                ...stateUpdate,
            };
        }

        case DRAW_EDIT_GET_PLAYERS_SUCCESS: {
            let { players } = action.payload;
            const { utrTypeId, eventDivisions: divisions } = state.event.data || {};
            const divisionsDict = {};
            if (divisions) {
                divisions.forEach(div => {
                    divisionsDict[div.id] = div;
                });
            }

            const { drawsDict } = state.draws;

            const playersDict = {}; // player dictionary by playerId
            const playersDictByMemberId = {}; // player dictionary by memberId

            const getDrawName = (draw) => {
                const drawsDictObj = drawsDict?.[draw.id];
                if (drawsDictObj?.isChildDraw) {
                    return `${drawsDict[drawsDictObj.parentDrawId]?.name} (${drawsDictObj?.name})`;
                }
                return drawsDictObj?.name;
            };

            players = players.map(player => {
                let playerDrawNames = '';
                (player.draws || []).forEach(draw => {
                    if (drawsDict?.[draw.id]?.name) {
                        playerDrawNames = playerDrawNames.length ? `${playerDrawNames}, ${getDrawName(draw)}` : getDrawName(draw);
                    }
                });
                (player.divisions || []).forEach(div => {
                    if (divisionsDict?.[div.id]?.name) {
                        div.name = divisionsDict?.[div.id]?.name;
                    }
                    if (divisionsDict?.[div.id]?.specificDivisionDateEnabled) {
                        div.specificDivisionDateEnabled = divisionsDict?.[div.id]?.specificDivisionDateEnabled;
                        div.specificDivisionDateUtc = divisionsDict?.[div.id]?.specificDivisionDateUtc;
                    }
                });
                const utrRangeDisplay = player.utrRange?.minUtrDisplay && player.utrRange?.maxUtrDisplay ? `${player.utrRange.minUtrDisplay}-${player.utrRange.maxUtrDisplay}` : null;
                player.age = getAgeByBirthDate(player.birthDate);
                return {
                    ...player,
                    utrRangeDisplay,
                    playerDrawNames,
                };
            });

            if (players?.length) {
                players.forEach(player => {
                    playersDict[player.playerId] = player;
                    playersDictByMemberId[player.memberId] = player;
                });
                _.orderBy(players, utrTypeId === utrTypeIds.SANCTIONED ? ['ratingStatusSinglesVal', 'utrSingles'] : ['myUtrSingles', 'utrRangeDisplay'], ['desc', 'desc']);
            }

            return {
                ...state,
                players: {
                    loading: false,
                    error: null,
                    data: players,
                    playersDict,
                    playersDictByMemberId,
                },
            };
        }

        case DRAW_EDIT_GET_PLAYERS_FAILURE: {
            return {
                ...state,
                players: {
                    ...state.players,
                    loading: false,
                    error: true,
                },
            };
        }

        case DRAW_EDIT_GET_TEAMS_SUCCESS: {
            let { teams } = action.payload;
            const { utrTypeId, eventDivisions: divisions } = state.event.data || {};
            const { data: players, playersDictByMemberId } = state.players;
            const divisionsDict = {};
            if (divisions) {
                divisions.forEach(div => {
                    divisionsDict[div.id] = div;
                });
            }

            const { drawsDict } = state.draws;
            const teamsDict = {};

            const getDrawName = (draw) => {
                const drawsDictObj = drawsDict?.[draw.id];
                if (drawsDictObj?.isChildDraw) {
                    return `${drawsDict[drawsDictObj.parentDrawId]?.name} (${drawsDictObj?.name})`;
                }
                return drawsDictObj?.name;
            };

            teams = teams.map(team => {
                let teamDrawNames = '';
                team.divisions = [];
                (team.draws || []).forEach(draw => {
                    if (drawsDict?.[draw.id]?.name) {
                        teamDrawNames = teamDrawNames.length ? `${teamDrawNames}, ${getDrawName(draw)}` : getDrawName(draw);
                    }
                });
                if (team.members) {
                    if (playersDictByMemberId[team.members[0]] && playersDictByMemberId[team.members[1]]) {
                        team.displayName = `${playersDictByMemberId[team.members[0]]?.firstName[0]}. ${playersDictByMemberId[team.members[0]]?.lastName} / ${playersDictByMemberId[team.members[1]]?.firstName[0]}. ${playersDictByMemberId[team.members[1]]?.lastName}`;
                        team.searchableName = `${playersDictByMemberId[team.members[0]]?.firstName} ${playersDictByMemberId[team.members[0]]?.lastName} ${playersDictByMemberId[team.members[1]]?.firstName} ${playersDictByMemberId[team.members[1]]?.lastName}`;
                    }
                    players.forEach(p => {
                        let playerDivisionObj;
                        if (p.memberId === team.members[0]) {
                            playerDivisionObj = p.divisions.filter(div => div.partners[0] === team.members[1]);
                            if (playerDivisionObj.length > 0) {
                                team.divisions = [
                                    ...(team.divisions || []),
                                    ...playerDivisionObj.map((div) => {
                                        return {
                                            id: div.id,
                                            name: div.name,
                                            specificDivisionDateEnabled: divisionsDict?.[div.id]?.specificDivisionDateEnabled || false,
                                            specificDivisionDateUtc: divisionsDict?.[div.id]?.specificDivisionDateUtc || null,
                                        };
                                    }),
                                ];
                            }
                        }
                    });
                }
                return {
                    ...team,
                    teamDrawNames,
                };
            });

            // doubles division registration teams are currently not stored by the api, only the memberId of the partner
            // so we have to create these teams in the client to display them in the draw teams lists
            // TODO: shouldn't these teams be stored on the api? review api design with @Siva

            players.forEach(p => {
                const memberId1 = p.memberId;
                // find their partner's ids in different divisions
                p.divisions.forEach(playerDivisionObj => {
                    const memberId2 = playerDivisionObj?.partners[0];
                    if (memberId2) {
                        const memberIds = [memberId1, memberId2];
                        let existingTeam;
                        teams.forEach(team => {
                            if (isEqual(team.members.sort((a, b) => a - b), memberIds.sort((a, b) => a - b))) {
                                existingTeam = true;
                                if (!team?.divisions?.some((div) => div.id === playerDivisionObj.id)) {
                                    team.divisions.push({
                                        id: playerDivisionObj.id,
                                        name: playerDivisionObj.name,
                                        specificDivisionDateEnabled: divisionsDict?.[playerDivisionObj.id]?.specificDivisionDateEnabled || false,
                                        specificDivisionDateUtc: divisionsDict?.[playerDivisionObj.id]?.specificDivisionDateUtc || null,
                                    });
                                }
                            }
                        });
                        if (!existingTeam) {
                            let displayName;
                            let searchableName;
                            if (playersDictByMemberId[memberId1] && playersDictByMemberId[memberId2]) {
                                displayName = `${playersDictByMemberId[memberId1]?.firstName[0]}. ${playersDictByMemberId[memberId1]?.lastName} / ${playersDictByMemberId[memberId2]?.firstName[0]}. ${playersDictByMemberId[memberId2]?.lastName}`;
                                searchableName = `${playersDictByMemberId[memberId1]?.firstName} ${playersDictByMemberId[memberId1]?.lastName} ${playersDictByMemberId[memberId2]?.firstName} ${playersDictByMemberId[memberId2]?.lastName}`;
                            }
                            const divisionRegTeam = {
                                id: newGuid(),
                                members: memberIds,
                                draws: [],
                                divisions: [{
                                    id: playerDivisionObj.id,
                                    name: playerDivisionObj.name,
                                    specificDivisionDateEnabled: divisionsDict?.[playerDivisionObj.id]?.specificDivisionDateEnabled || false,
                                    specificDivisionDateUtc: divisionsDict?.[playerDivisionObj.id]?.specificDivisionDateUtc || null,
                                }],
                                unsaved: true,
                                displayName,
                                searchableName,
                            };
                            teams.push(divisionRegTeam);
                        }
                    }
                });
            });

            if (teams?.length) {
                teams.forEach(team => {
                    teamsDict[team.id] = team;
                });
                _.orderBy(teams, utrTypeId === utrTypeIds.SANCTIONED ? 'averageDoublesUtr' : 'averageDoublesMyUtr', 'desc');
            }

            return {
                ...state,
                teams: {
                    loading: false,
                    error: null,
                    data: teams,
                    teamsDict,
                },
            };
        }

        case DRAW_EDIT_GET_ACCEPTANCE_LIST: {
            return {
                ...state,
                acceptance: {
                    ...(state.acceptance || {}),
                    [action.divisionId]: {
                        loading: true,
                        loaded: false,
                        error: null,
                    },
                },
            };
        }
        case DRAW_EDIT_GET_ACCEPTANCE_LIST_FAILURE: {
            return {
                ...state,
                acceptance: {
                    ...(state.acceptance || {}),
                    [action.divisionId]: {
                        loading: false,
                        loaded: false,
                        error: 'Failed to load acceptance list',
                    },
                },
            };
        }
        case DRAW_EDIT_GET_ACCEPTANCE_LIST_SUCCESS: {
            return {
                ...state,
                playersTabState: {
                    ...state.currentEvent.playersTabState,
                    sortAcceptanceType: acceptanceRankingValues.WTA,
                    sortListParam: 'wtaOrder',
                    sortListType: SortType.ASC,
                },
                acceptance: {
                    ...(state.currentEvent.acceptance || {}),
                    [action.divisionId]: {
                        ...action.data,
                        loading: false,
                        loaded: true,
                        error: null,
                    },
                },
            };
        }
        case DRAW_EDIT_GET_ORGANIZER_RATINGS_SUCCESS: {
            return {
                ...state,
                organizerRatings: action.payload,
            };
        }
        case DRAW_EDIT_GET_DRAW_SUCCESS: {
            const { draw, isChildDraw, setAsCurrentDraw } = action.payload;

            const stateUpdate = {
                selected: [],
                selectedNotInDraw: [],
                selectedTeams: [],
                selectedTeamsNotInDraw: [],
            };
            const isGroupedDraw = draw.categoryTypeId === drawCategoryTypeIds.GROUPED;

            draw.rounds.forEach(round => {
                round.matches = sortBy(round.matches, 'matchPosition');
            });

            if (isChildDraw) {
                // load childDraw as currentDraw (keep existing parentDraw)
                stateUpdate.currentDraw = {
                    loading: false,
                    error: null,
                    data: {
                        ...draw,
                        rounds: sortBy(draw.rounds, 'number'),
                    },
                    isChildDraw,
                };
            } else {
                // load parentDraw
                stateUpdate.parentDraw = {
                    loading: false,
                    error: null,
                    data: {
                        ...draw,
                        rounds: sortBy(draw.rounds, 'number'),
                    },
                    isGroupedDraw,
                };
                if (!isGroupedDraw && setAsCurrentDraw) {
                    // if not groupedDraw, also set parentDraw as the currentDraw
                    // if grouped parentDraw, then child first child needs to be loaded automatically
                    // (handled in the action)
                    stateUpdate.currentDraw = {
                        loading: false,
                        error: null,
                        data: {
                            ...draw,
                            rounds: sortBy(draw.rounds, 'number'),
                        },
                    };
                }
            }

            const drawsData = state.draws.data;
            const currentDrawIndex = drawsData.findIndex(d => d.id === draw.id);
            if (currentDrawIndex !== -1) {
                drawsData[currentDrawIndex].canDeleteDraw = draw.canDeleteDraw;
            }

            return {
                ...state,
                draws: {
                    ...state.draws,
                    data: drawsData,
                },
                ...stateUpdate,
            };
        }

        case DRAW_EDIT_ADD_TO_DRAW: {
            const { selectedNotInDraw = [], selectedTeamsNotInDraw = [] } = state;
            const membersToAdd = [];
            const teamsToAdd = [];

            // singles

            selectedNotInDraw.forEach(memberId => {
                membersToAdd.push({
                    memberId,
                    drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED,
                    tagId: null,
                    unsaved: true,
                });
            });

            // doubles

            selectedTeamsNotInDraw.forEach(teamId => {
                teamsToAdd.push({
                    teamId,
                    drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED,
                    tagId: null,
                    unsaved: true,
                });
            });

            const updatedDrawData = {
                ...state.currentDraw.data,
                members: [...state.currentDraw.data.members, ...membersToAdd],
                teams: [...state.currentDraw.data.teams, ...teamsToAdd],
            };

            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDrawData,
                    unsavedChanges: true,
                },
            };
        }

        case DRAW_EDIT_REMOVE_FROM_DRAW: {
            const { selected, selectedTeams } = action.payload;
            const drawId = state.currentDraw.data.id;
            let updatedEventTeams;
            const updatedRounds = cloneDeep(state.currentDraw.data.rounds || []);
            const drawUpdate = {};
            let roundsAreUpdated = false;
            let eventTeamsAreUpdated = false;

            // singles

            if (selected.length) {
                // remove players from draw
                const updatedMembers = state.currentDraw.data.members.filter(m => !selected.includes(m.memberId));
                updatedRounds.forEach(round => {
                    round.matches.forEach(match => {
                        if (match.position1 && selected.includes(parseInt(match.position1.memberId, 10))) {
                            match.position1 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                        if (match.position2 && selected.includes(parseInt(match.position2.memberId, 10))) {
                            match.position2 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                    });
                });

                drawUpdate.members = updatedMembers;
            }

            // doubles

            if (selectedTeams.length) {
                // remove teams from draw
                const updatedTeams = state.currentDraw.data.teams.filter(t => !selectedTeams.includes(t.teamId));
                // if team is not added to any other draw, remove it from the event
                updatedEventTeams = state.teams.data.filter(t => !(t.draws.includes(drawId) && t.draws.length === 1));
                eventTeamsAreUpdated = true;
                updatedRounds.forEach(round => {
                    round.matches.forEach(match => {
                        if (match.position1 && selectedTeams.includes(match.position1.teamId)) {
                            match.position1 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                        if (match.position2 && selectedTeams.includes(match.position2.teamId)) {
                            match.position2 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                    });
                });

                drawUpdate.teams = updatedTeams;
            }

            if (roundsAreUpdated) {
                drawUpdate.rounds = updatedRounds;
            }

            const updatedDrawData = {
                ...state.currentDraw.data,
                ...drawUpdate,
            };

            const stateUpdate = {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDrawData,
                    unsavedChanges: true,
                },
            };

            if (eventTeamsAreUpdated && updatedEventTeams) {
                stateUpdate.teams = {
                    ...state.teams,
                    data: updatedEventTeams,
                };
            }

            return stateUpdate;
        }
        // create team from 2 selected members
        case DRAW_EDIT_CREATE_TEAM: {
            const teamMembers = state.selected;
            teamMembers.sort((a, b) => a - b);
            let teamToAdd = state.teams.data.find(team => {
                team.members.sort((a, b) => a - b);
                return isEqual(teamMembers, team.members);
            });
            let isNewTeam;

            if (!teamToAdd) {
                // new team
                let displayName;
                let searchableName;
                if (state.players.playersDictByMemberId[teamMembers[0]] && state.players.playersDictByMemberId[teamMembers[1]]) {
                    displayName = `${state.players.playersDictByMemberId[teamMembers[0]]?.firstName[0]}. ${state.players.playersDictByMemberId[teamMembers[0]]?.lastName} / ${state.players.playersDictByMemberId[teamMembers[1]]?.firstName[0]}. ${state.players.playersDictByMemberId[teamMembers[1]]?.lastName}`;
                    searchableName = `${state.players.playersDictByMemberId[teamMembers[0]]?.firstName} ${state.players.playersDictByMemberId[teamMembers[0]]?.lastName} ${state.players.playersDictByMemberId[teamMembers[1]]?.firstName} ${state.players.playersDictByMemberId[teamMembers[1]]?.lastName}`;
                }
                teamToAdd = {
                    id: newGuid(),
                    members: teamMembers,
                    draws: [{ id: state.currentDraw.data.id, drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED, tagId: null }],
                    unsaved: true,
                    displayName,
                    searchableName,
                };
                isNewTeam = true;
            } else {
                teamToAdd.draws.push({ id: state.currentDraw.data.id, drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED, tagId: null });
            }

            // remove team members from draw (AVAILABLE section)
            const updatedMembers = state.currentDraw.data.members.filter(m => !teamMembers.includes(m.memberId));

            const updatedTeams = {
                ...state.teams,
                data: isNewTeam ? [...state.teams.data, teamToAdd] : state.teams.data,
                teamsDict: {
                    ...state.teams.teamsDict || {},
                    [teamToAdd.id]: teamToAdd,
                },
            };

            const updatedDrawData = {
                ...state.currentDraw.data,
                members: updatedMembers,
                teams: [...state.currentDraw.data.teams, {
                    teamId: teamToAdd.id,
                    drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED,
                    tagId: null,
                }],
            };

            return {
                ...state,
                teams: updatedTeams,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDrawData,
                    unsavedChanges: true,
                },
            };
        }

        case DRAW_EDIT_ADD_TEAM_TO_EVENT: {
            const { teamToAdd } = action.payload;
            return {
                ...state,
                teams: {
                    ...state.teams,
                    data: [...state.teams.data, teamToAdd],
                    teamsDict: {
                        ...state.teams.teamsDict || {},
                        [teamToAdd.id]: teamToAdd,
                    },
                },
            };
        }

        case DRAW_EDIT_SPLIT_TEAMS: {
            const { selectedTeams } = action.payload;
            const updatedRounds = cloneDeep(state.currentDraw.data.rounds || []);
            const drawUpdate = {};
            let roundsAreUpdated = false;

            // remove teams from draw
            const updatedTeams = state.currentDraw.data.teams.filter(t => !selectedTeams.includes(t.teamId));
            // if teams is not added to any other draw, remove it from the event
            // const updatedEventTeams = state.teams.data.filter(t => !(selectedTeams.includes(t.teamId) && t.draws.length === 1));
            // remove teams from matches
            updatedRounds.forEach(round => {
                round.matches.forEach(match => {
                    if (match.position1 && selectedTeams.includes(parseInt(match.position1.teamId, 10))) {
                        match.position1 = null;
                        match.unsaved = true;
                        roundsAreUpdated = true;
                    }
                    if (match.position2 && selectedTeams.includes(parseInt(match.position2.teamId, 10))) {
                        match.position2 = null;
                        match.unsaved = true;
                        roundsAreUpdated = true;
                    }
                });
            });
            // add split team's members to draw members array (AVAILABLE section)
            const membersToAdd = [];
            state.teams.data.forEach(t => {
                if (selectedTeams.includes(t.id)) {
                    t.members.forEach(memberId => {
                        membersToAdd.push({
                            memberId,
                            drawPlacementStatusId: drawPlacementStatusTypeIds.NOT_PLACED,
                            tagId: null,
                            unsaved: true,
                        });
                    });
                }
            });

            drawUpdate.teams = updatedTeams;
            drawUpdate.members = [...state.currentDraw.data.members, ...membersToAdd];

            if (roundsAreUpdated) {
                drawUpdate.rounds = updatedRounds;
            }

            const updatedDrawData = {
                ...state.currentDraw.data,
                ...drawUpdate,
            };

            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDrawData,
                    unsavedChanges: true,
                },
                // teams: {
                //     ...state.teams,
                //     data: updatedEventTeams,
                // },
            };
        }

        case DRAW_EDIT_ADD_TO_MATCH: {
            const { roundIndex, matchId, position, player } = action.payload;
            const { formatTypeId } = state.currentDraw.data;
            const updatedRounds = cloneDeep(state.currentDraw.data.rounds || []);
            const updatedMembers = cloneDeep(state.currentDraw.data.members || []);
            const updatedTeams = cloneDeep(state.currentDraw.data.teams || []);
            const isMatchPlay = state.currentDraw.data.eliminationTypeId === drawEliminationTypeIds.MATCH_PLAY;

            const match = updatedRounds[roundIndex].matches?.find(m => m.id === matchId);
            let prevMemberId;
            let prevTeamId;
            if (match) {
                if (formatTypeId === eventFormatTypeIds.SINGLES) {
                    // singles
                    if (match[position]?.memberId) {
                        prevMemberId = match[position]?.memberId;
                    }
                    if (!player) { // clear
                        match[position] = null;
                    } else if (player.typeId === drawPositionTypeIds.BYE) { // bye
                        match[position] = {
                            typeId: drawPositionTypeIds.BYE,
                        };
                    } else { // add player
                        match[position] = {
                            memberId: player.memberId,
                            typeId: drawPositionTypeIds.PLAYER,
                        };
                        // remove player from the other match position
                        if (match[position === 'position1' ? 'position2' : 'position1']?.memberId === player.memberId) {
                            match[position === 'position1' ? 'position2' : 'position1'] = null;
                        }
                        // if not MATCH PLAY, also remove the player from other matches within the same round
                        if (!isMatchPlay) {
                            updatedRounds[roundIndex].matches.forEach(m => {
                                if (m.id !== matchId) {
                                    if (m.position1?.memberId === player.memberId) {
                                        m.position1 = null;
                                        m.unsaved = true;
                                    }
                                    if (m.position2?.memberId === player.memberId) {
                                        m.position2 = null;
                                        m.unsaved = true;
                                    }
                                }
                            });
                        }
                        const member = updatedMembers.find(m => m.memberId === player.memberId);
                        if (member) {
                            member.drawPlacementStatusId = drawPlacementStatusTypeIds.PLACED;
                            member.unsaved = true;
                        }
                    }
                    if (prevMemberId && !isMemberPlaced(prevMemberId, updatedRounds)) {
                        // if the previous member in that position is not placed anywhere in the draw anymore, update status
                        const prevMember = updatedMembers.find(m => m.memberId === prevMemberId);
                        if (prevMember) {
                            prevMember.drawPlacementStatusId = drawPlacementStatusTypeIds.NOT_PLACED;
                            prevMember.unsaved = true;
                        }
                    }
                } else {
                    // doubles
                    if (match[position]?.teamId) {
                        prevTeamId = match[position]?.teamId;
                    }
                    if (!player) { // clear
                        match[position] = null;
                    } else if (player.typeId === drawPositionTypeIds.BYE) { // bye
                        match[position] = {
                            typeId: drawPositionTypeIds.BYE,
                        };
                    } else { // add team
                        match[position] = {
                            teamId: player.id,
                            typeId: drawPositionTypeIds.TEAM,
                        };
                        // remove team from the other match position
                        if (match[position === 'position1' ? 'position2' : 'position1']?.teamId === player.id) {
                            match[position === 'position1' ? 'position2' : 'position1'] = null;
                        }
                        // if not MATCH PLAY, also remove the team from OTHER matches within the same round
                        // and remove the other teams the players are in, from ALL the matches
                        if (!isMatchPlay) {
                            const otherTeamsOfMembers = getOtherTeamsOfTeamMembers(player.id, state.teams.data);
                            updatedRounds[roundIndex].matches.forEach(m => {
                                if (m.id !== matchId) {
                                    if (m.position1?.teamId && (m.position1?.teamId === player.id || otherTeamsOfMembers.includes(m.position1?.teamId))) {
                                        m.position1 = null;
                                        m.unsaved = true;
                                    }
                                    if (m.position2?.teamId && (m.position2?.teamId === player.id || otherTeamsOfMembers.includes(m.position2?.teamId))) {
                                        m.position2 = null;
                                        m.unsaved = true;
                                    }
                                } else {
                                    if (m.position1?.teamId && otherTeamsOfMembers.includes(m.position1?.teamId)) {
                                        m.position1 = null;
                                        m.unsaved = true;
                                    }
                                    if (m.position2?.teamId && otherTeamsOfMembers.includes(m.position2?.teamId)) {
                                        m.position2 = null;
                                        m.unsaved = true;
                                    }
                                }
                            });
                        }
                        const team = updatedTeams.find(t => t.teamId === player.id);
                        if (team) {
                            team.drawPlacementStatusId = drawPlacementStatusTypeIds.PLACED;
                            team.unsaved = true;
                        }
                    }
                    if (prevTeamId && !isTeamPlaced(prevTeamId, updatedRounds)) {
                        // if the previous team in the position is not placed anywhere in the draw anymore, update status
                        const prevTeam = updatedTeams.find(t => t.teamId === prevTeamId);
                        if (prevTeam) {
                            prevTeam.drawPlacementStatusId = drawPlacementStatusTypeIds.NOT_PLACED;
                            prevTeam.unsaved = true;
                        }
                    }
                }
                match.unsaved = true;
            }

            const drawUpdate = {
                rounds: updatedRounds,
            };
            if (formatTypeId === eventFormatTypeIds.SINGLES) {
                drawUpdate.members = updatedMembers;
            } else {
                drawUpdate.teams = updatedTeams;
            }

            const updatedDrawData = {
                ...state.currentDraw.data,
                ...drawUpdate,
            };

            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDrawData,
                    unsavedChanges: true,
                },
            };
        }

        case DRAW_EDIT_SET_SELECTED: {
            return {
                ...state,
                selected: action.payload,
            };
        }
        case DRAW_EDIT_SET_SELECTED_ND: {
            return {
                ...state,
                selectedNotInDraw: action.payload,
            };
        }
        case DRAW_EDIT_SET_SELECTED_TEAMS: {
            return {
                ...state,
                selectedTeams: action.payload,
            };
        }
        case DRAW_EDIT_SET_SELECTED_TEAMS_ND: {
            return {
                ...state,
                selectedTeamsNotInDraw: action.payload,
            };
        }
        case DRAW_EDIT_SET_PREVENT_SELECT_MOVE: {
            return {
                ...state,
                preventSelectMove: action.payload,
            };
        }
        case DRAW_EDIT_ORDER_PLAYERS: {
            const { organizerRatings } = state;
            const { sortParam, sortType, formatTypeId, utrTypeId, seedsArray } = action.payload;

            let orderedArr = formatTypeId === eventFormatTypeIds.SINGLES ? cloneDeep(state.players.data) : cloneDeep(state.teams.data);

            const getSortValue = (player) => {
                const userIsPowered = true;
                const showDecimals = true;
                switch (sortParam) {
                    case sortParamTypes.SINGLESUTR:
                    case 'regStatus': {
                        const organizerRating = getOrganizerRating(player?.memberId, organizerRatings)?.singlesOrganizerRating;
                        const playerSinglesRating = getRatingDetails(setRatingChoice({ player, ratingType: gameTypes.SINGLES, utrType: utrTypeId }), userIsPowered, showDecimals);
                        return organizerRating || playerSinglesRating.sortRating;
                    }
                    case sortParamTypes.DOUBLESUTR: {
                        const organizerRating = getOrganizerRating(player?.memberId, organizerRatings)?.doublesOrganizerRating;
                        const playerDoublesRating = getRatingDetails(setRatingChoice({ player, ratingType: gameTypes.DOUBLES, utrType: utrTypeId }), userIsPowered, showDecimals);
                        return organizerRating || playerDoublesRating.sortRating;
                    }
                    case sortParamTypes.PKBRATING: {
                        const organizerRating = getOrganizerRating(player?.memberId, organizerRatings)?.singlesOrganizerRating;
                        const playerPickleBallRating = getPickleBallRating(player);
                        return organizerRating || playerPickleBallRating.sortRating;
                    }
                    case sortParamTypes.SINGLESPKB: {
                        const organizerRating = getOrganizerRating(player?.memberId, organizerRatings)?.singlesOrganizerRating;
                        const playerPickleBallRating = getPickleBallRating(player, gameTypes.SINGLES, true);
                        return organizerRating || playerPickleBallRating.sortRating;
                    }
                    case sortParamTypes.DOUBLESPKB: {
                        const organizerRating = getOrganizerRating(player?.memberId, organizerRatings)?.doublesOrganizerRating;
                        const teamPickleBallRating = getPickleBallRating(player, gameTypes.DOUBLES, true);
                        return organizerRating || teamPickleBallRating.sortRating;
                    }
                    default:
                        return player[sortParam];
                }
            };
            if (sortParam === 'seed') {
                if (formatTypeId === eventFormatTypeIds.SINGLES) {
                    const playersWithSeedOrder = orderedArr.filter((player) => seedsArray.some(item => item.memberId === player.memberId));
                    const playersWithoutSeedOrder = orderedArr.filter((player) => !seedsArray.some(item => item.memberId === player.memberId));
                    seedsArray.forEach((item) => {
                        playersWithSeedOrder.find((player) => player.memberId === item.memberId).seedNumber = item.seedNumber;
                    });
                    playersWithSeedOrder.sort((a, b) => {
                        if (a.seedNumber === null && b.seedNumber === null) { return 0; }
                        if (a.seedNumber === null) { return 1; }
                        if (b.seedNumber === null) { return -1; }
                        return sortType === 'asc' ? (a.seedNumber - b.seedNumber) : (b.seedNumber - a.seedNumber);
                    });
                    orderedArr = [...playersWithSeedOrder, ...playersWithoutSeedOrder];
                } else {
                    const teamsWithSeedOrder = orderedArr.filter((team) => seedsArray.some(item => item.teamId === team.id));
                    const teamsWithoutSeedOrder = orderedArr.filter((team) => !seedsArray.some(item => item.teamId === team.id));
                    seedsArray.forEach((item) => {
                        teamsWithSeedOrder.find((team) => team.id === item.teamId).seedNumber = item.seedNumber;
                    });
                    teamsWithSeedOrder.sort((a, b) => {
                        if (a.seedNumber === null && b.seedNumber === null) { return 0; }
                        if (a.seedNumber === null) { return 1; }
                        if (b.seedNumber === null) { return -1; }
                        return sortType === 'asc' ? (a.seedNumber - b.seedNumber) : (b.seedNumber - a.seedNumber);
                    });
                    orderedArr = [...teamsWithSeedOrder, ...teamsWithoutSeedOrder];
                }
            } else if (formatTypeId === eventFormatTypeIds.SINGLES) {
                    orderedArr.forEach((player) => {
                        switch (sortParam) {
                            case 'firstName':
                                player.primarySortValue = player.firstName;
                                break;
                            case 'lastName':
                                player.primarySortValue = player.lastName;
                                break;
                            case sortParamTypes.SINGLESPKB:
                            case sortParamTypes.DOUBLESPKB:
                            case sortParamTypes.PKBRATING:
                            case sortParamTypes.SINGLESUTR:
                            case sortParamTypes.DOUBLESUTR: {
                                player.primarySortValue = getSortValue(player);
                                break;
                            }
                            default:
                                player.primarySortValue = player.regStatusSortKey;
                                break;
                        }
                    });
                    orderedArr.sort(comparator(getComparatorArguments(sortParam, sortType)));
                } else {
                    orderedArr.forEach((team) => {
                        const player1 = state.players.playersDictByMemberId[team.members[0]];
                        const player2 = state.players.playersDictByMemberId[team.members[1]];
                        switch (sortParam) {
                            case 'firstName':
                                team.primarySortValue = `${player1.firstName} ${player2.firstName}`;
                                break;
                                case sortParamTypes.SINGLESPKB:
                                case sortParamTypes.DOUBLESPKB:
                                case sortParamTypes.PKBRATING:
                                case sortParamTypes.SINGLESUTR:
                                case sortParamTypes.DOUBLESUTR: {
                                const player1Rating = getSortValue(player1);
                                const player2Rating = getSortValue(player2);
                                team.primarySortValue = (parseFloat(player1Rating) + parseFloat(player2Rating)) / 2;
                                break;
                            }
                            default:
                                team.primarySortValue = '';
                                break;
                        }
                    });
                    orderedArr.sort(comparator(getComparatorArguments(sortParam, sortType)));
            }
            if (formatTypeId === eventFormatTypeIds.SINGLES) {
                return {
                    ...state,
                    players: {
                        ...state.players,
                        data: orderedArr,
                    },
                };
            }
            return {
                ...state,
                teams: {
                    ...state.teams,
                    data: orderedArr,
                },
            };
        }

        case DRAW_EDIT_SET_PLAYER_STATUS: {
            const { value, memberId, teamId } = action.payload;
            const updatedRounds = cloneDeep(state.currentDraw.data.rounds || []);
            const drawUpdate = {};
            let roundsAreUpdated;
            let newDrawPlacementStatusId;

            if (value === 'not_placed') {
                newDrawPlacementStatusId = drawPlacementStatusTypeIds.NOT_PLACED;
            } else if (value === 'waitlist') {
                newDrawPlacementStatusId = drawPlacementStatusTypeIds.WAITLIST;
            }

            // singles
            if (memberId) {
                // remove member from match positions
                updatedRounds.forEach(round => {
                    round.matches.forEach(match => {
                        if (match.position1?.memberId === memberId) {
                            match.position1 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                        if (match.position2?.memberId === memberId) {
                            match.position2 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                    });
                });
                // update drawPlacementStatusId on members
                drawUpdate.members = state.currentDraw.data.members.map(member => {
                    if (member.memberId === memberId) {
                        member.drawPlacementStatusId = newDrawPlacementStatusId;
                        member.unsaved = true;
                    }
                    return member;
                });
            }
            // doubles
            if (teamId) {
                // remove member from match positions
                updatedRounds.forEach(round => {
                    round.matches.forEach(match => {
                        if (match.position1?.teamId === teamId) {
                            match.position1 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                        if (match.position2?.teamId === teamId) {
                            match.position2 = null;
                            match.unsaved = true;
                            roundsAreUpdated = true;
                        }
                    });
                });
                // update drawPlacementStatusId on teams
                drawUpdate.teams = state.currentDraw.data.teams.map(team => {
                    if (team.teamId === teamId) {
                        team.drawPlacementStatusId = newDrawPlacementStatusId;
                        team.unsaved = true;
                    }
                    return team;
                });
            }

            if (roundsAreUpdated) {
                drawUpdate.rounds = updatedRounds;
            }

            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: {
                        ...state.currentDraw.data,
                        ...drawUpdate,
                    },
                    unsavedChanges: true,
                },
            };
        }

        case DRAW_EDIT_SET_DRAW_TAG_SUCCESS: {
            const { memberId, teamId, tagId } = action.payload;
            const drawUpdate = {};

            // singles
            if (memberId) {
                // update drawPlacementStatusId on members
                drawUpdate.members = state.currentDraw.data.members.map(member => {
                    if (member.memberId === memberId) {
                        member.tagId = tagId;
                    }
                    return member;
                });
            }
            // doubles
            if (teamId) {
                // remove member from match positions
                // update drawPlacementStatusId on teams
                drawUpdate.teams = state.currentDraw.data.teams.map(team => {
                    if (team.teamId === teamId) {
                        team.tagId = tagId;
                    }
                    return team;
                });
            }

            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: {
                        ...state.currentDraw.data,
                        ...drawUpdate,
                    },
                },
            };
        }

        // ---------------------------------------------------------------------

        case DRAW_EDIT_UPDATE_DRAWS: {
            const { draws, registeredPlayers, teams } = action.payload;
            const event = state.event.data;

            let playersDict;
            if (registeredPlayers?.length) {
                playersDict = {};
                registeredPlayers.forEach(player => {
                    playersDict[player.id] = player;
                });
                _.orderBy(registeredPlayers, event.utrType.value === 'sanctioned' ? ['ratingStatusSinglesVal', 'utrSingles'] : 'myUtrSingles', ['desc', 'desc']);
            }

            let teamsDict;
            if (teams?.length) {
                teamsDict = {};
                teams.forEach(team => {
                    teamsDict[team.id] = team;
                });
                _.orderBy(teams, event.utrType.value === 'sanctioned' ? 'averageDoublesUtr' : 'averageDoublesMyUtr', 'desc');
            }

            return {
                ...state,
                draws: {
                    ...state.draws,
                    data: draws,
                },
                player: {
                    ...state.players,
                    data: registeredPlayers,
                    ...(playersDict ? { playersDict } : {}),
                },
                teams: {
                    ...state.teams,
                    data: teams,
                    ...(teamsDict ? { teamsDict } : {}),
                },
            };
        }

        case DRAW_EDIT_LOCATIONS_FETCH_REQUEST: {
            return {
                ...state,
                locations: {
                    loading: true,
                    error: null,
                    data: null,
                },
            };
        }
        case DRAW_EDIT_LOCATIONS_FETCH_FAILURE: {
            return {
                ...state,
                locations: {
                    loading: false,
                    error: 'Failed to load locations',
                    data: null,
                },
            };
        }
        case DRAW_EDIT_LOCATIONS_FETCH_SUCCESS: {
            const { locations } = action.data;
            locations.forEach(loc => {
                loc.facilities.forEach(fac => {
                    fac.enabled = fac.courts.some(court => court.enabled);
                });
                loc.enabled = loc.facilities.some(fac => fac.enabled);
            });
            return {
                ...state,
                locations: {
                    loading: false,
                    error: null,
                    data: locations,
                },
            };
        }

        case DRAW_EDIT_FACILITIES_FETCH_REQUEST: {
            return {
                ...state,
                eventFacilities: {
                    loading: true,
                    error: null,
                    data: null,
                },
            };
        }
        case DRAW_EDIT_FACILITIES_FETCH_FAILURE: {
            return {
                ...state,
                eventFacilities: {
                    loading: false,
                    error: 'Failed to load event facilities',
                    data: null,
                },
            };
        }
        case DRAW_EDIT_FACILITIES_FETCH_SUCCESS: {
            const eventFacilities = action.data;
            return {
                ...state,
                eventFacilities: {
                    loading: false,
                    error: null,
                    data: eventFacilities,
                },
            };
        }
        case DRAW_EDIT_LOCATIONS_EXCLUDE_REQUEST: {
            return {
                ...state,
                locations: {
                    ...state.locations,
                    updating: true,
                },
            };
        }
        case DRAW_EDIT_LOCATIONS_EXCLUDE_SUCCESS:
        case DRAW_EDIT_LOCATIONS_EXCLUDE_FAILURE: {
            return {
                ...state,
                locations: {
                    ...state.locations,
                    updating: false,
                },
            };
        }

        case DRAW_EDIT_OFFICIALS_FETCH_REQUEST: {
            return {
                ...state,
                officials: {
                    loading: true,
                    error: null,
                    data: null,
                },
            };
        }
        case DRAW_EDIT_OFFICIALS_FETCH_FAILURE: {
            return {
                ...state,
                officials: {
                    loading: false,
                    error: 'Failed to load officials',
                    data: null,
                },
            };
        }
        case DRAW_EDIT_OFFICIALS_FETCH_SUCCESS: {
            const { officials } = action.payload;
            return {
                ...state,
                officials: {
                    loading: false,
                    error: null,
                    data: officials,
                },
            };
        }

        case DRAW_EDIT_GET_DRAW_OFFICIALS_SUCCESS: {
            return {
                ...state,
                drawOfficials: {
                    ...state.drawOfficials,
                    data: action.payload.data.matches,
                },
            };
        }

        case DRAW_EDIT_FETCH_MATCH_SHEET_SUCCESS: {
            return {
                ...state,
                matchSheet: {
                    ...state.matchSheet,
                    data: action.data,
                },
                orderOfPlay: {
                    ...state.orderOfPlay,
                    data: null,
                },
            };
        }

        case DRAW_EDIT_DRAW_CONTACTS_SUCCESS: {
            return {
                ...state,
                contacts: action.payload,
            };
        }

        case DRAW_EDIT_EDIT_TEAM_STANDING_SUCCESS: {
            return {
                ...state,
                teamStandings: state.teamStandings.map(standing => {
                    if (standing.id === action.payload.id) {
                        return { ...action.payload };
                    }
                    return standing;
                }),
            };
        }

        case DRAW_EDIT_DELETE_TEAM_STANDING_SUCCESS: {
            return {
                ...state,
                teamStandings: state.teamStandings.filter(({ id }) => id !== action.payload),
            };
        }

        case DRAW_EDIT_EVENT_TEAM_STANDINGS_SUCCESS: {
            return {
                ...state,
                teamStandings: action.payload,
            };
        }

        case DRAW_EDIT_ADD_TEAM_STANDING_SUCCESS: {
            return {
                ...state,
                teamStandings: [
                    ...(state.teamStandings || []),
                    action.payload,
                ],
            };
        }

        case DRAW_EDIT_UPDATE_MATCH_SHEET_SUCCESS: {
            const updatedDraw = cloneDeep(state.currentDraw.data);
            const { matchId, payload } = action.data;
            updatedDraw.rounds.forEach(round => {
                round.matches.forEach(match => {
                    if (match.id === matchId) {
                        match.date = payload.date;
                    }
                });
            });
            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }
        case DRAW_EDIT_UPDATE_MATCH_SHEET_ROUND_SUCCESS: {
            const updatedDraw = cloneDeep(state.currentDraw.data);
            const { roundId, payload } = action.data;
            updatedDraw.rounds.forEach(round => {
                if (round.id === roundId) {
                    round.matches.forEach(match => {
                        match.date = payload.date;
                    });
                }
            });
            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }
        case DRAW_EDIT_DELETE_MATCH_SHEET_SUCCESS: {
            const updatedDraw = cloneDeep(state.currentDraw.data);
            const { matchId } = action.payload;
            updatedDraw.rounds.forEach(round => {
                round.matches.forEach(match => {
                    if (match.id === matchId) {
                        match.date = null;
                    }
                });
            });
            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }
        case DRAW_EDIT_GET_ORDER_OF_PLAY_SUCCESS: {
            return {
                ...state,
                orderOfPlay: {
                    ...state.orderOfPlay,
                    data: action.payload,
                },
                matchSheet: {
                    ...state.matchSheet,
                    data: [],
                },
            };
        }
        case DRAW_EDIT_GET_ORDER_OF_PLAY_MATCHES_SUCCESS: {
            const { matches } = action.payload;
            return {
                ...state,
                orderOfPlay: {
                    ...state.orderOfPlay,
                    matches,
                },
            };
        }
        case DRAW_EDIT_SAVE_ORDER_OF_PLAY_SUCCESS: {
            const { matchId, matches } = action.payload;
            const updatedMatch = matches.find(m => m.matchId === matchId);
            const updatedDraw = cloneDeep(state.currentDraw.data);
            updatedDraw.rounds.forEach(round => {
                round.matches.forEach(match => {
                    if (match.id === matchId) {
                        match.date = updatedMatch.date;
                    }
                });
            });
            return {
                ...state,
                orderOfPlay: {
                    ...state.orderOfPlay,
                    data: unionBy(state.orderOfPlay.data, matches, 'matchId'),
                },
                matchSheet: {
                    ...state.matchSheet,
                    data: [],
                },
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }
        case DRAW_EDIT_DELETE_ORDER_OF_PLAY_SUCCESS: {
            const { matchId } = action.payload;
            const updatedDraw = cloneDeep(state.currentDraw.data);
            updatedDraw.rounds.forEach(round => {
                round.matches.forEach(match => {
                    if (match.id === matchId) {
                        match.date = null;
                    }
                });
            });
            return {
                ...state,
                orderOfPlay: {
                    ...state.orderOfPlay,
                    data: state.orderOfPlay.data.filter(oopObj => oopObj.matchId !== matchId),
                },
                matchSheet: {
                    ...state.matchSheet,
                    data: [],
                },
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }
        case DRAW_EDIT_GET_MATCH_OFFICIALS_SUCCESS: {
            const { matchId, data } = action.payload;
            const newMatchDetails = {
                id: matchId,
                officials: data,
            };
            return {
                ...state,
                drawOfficials: {
                    ...state.drawOfficials,
                    data: [...state.drawOfficials, newMatchDetails],
                },
            };
        }

        case DRAW_EDIT_SAVE: {
            return {
                ...state,
                isSaving: true,
                savingError: false,
            };
        }
        case DRAW_EDIT_SAVE_SUCCESS: {
            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    unsavedChanges: false,
                },
                isSaving: false,
                savingError: false,
                isSaved: true,
            };
        }
        case DRAW_EDIT_SAVE_SUCCESS_HIDE: {
            return {
                ...state,
                isSaved: false,
            };
        }
        case DRAW_EDIT_SAVE_FAILURE: {
            return {
                ...state,
                isSaving: false,
                savingError: true,
            };
        }
        case DRAW_EDIT_SET_SAVING_FLAG: {
            return {
                ...state,
                isSaving: action.payload,
            };
        }
        case DRAW_EDIT_GET_MATCH_SUCCESS: {
            const { id, roundId } = action.payload;
            const updatedDraw = cloneDeep(state.currentDraw.data);

            updatedDraw.rounds.forEach(round => {
                if (round.id === roundId) {
                    round.matches = round.matches.map(match => {
                        if (match.id === id) {
                            return { ...action.payload };
                        }
                        return match;
                    });
                }
            });
            return {
                ...state,
                currentDraw: {
                    ...state.currentDraw,
                    data: updatedDraw,
                },
            };
        }

        case DRAW_EDIT_UPDATE_SCHEDULE_TYPE: {
            const scheduleType = action.payload;
            return {
                ...state,
                event: {
                    ...state.event,
                    data: {
                        ...state.event.data,
                        scheduleTypeId: scheduleType.id,
                    },
                },
            };
        }
        case DRAW_EDIT_SET_RESET_TIMER: {
            return {
                ...state,
                resetTimer: action.payload,
            };
        }
        case DRAW_EDIT_PUBLISH: {
            return {
                ...state,
                isPublishing: true,
            };
        }
        case DRAW_EDIT_PUBLISH_SUCCESS:
        case DRAW_EDIT_PUBLISH_FAILURE: {
            return {
                ...state,
                isPublishing: false,
            };
        }
        case DRAW_EDIT_SET_MOVE_FAILED_PLAYERS: {
            return {
                ...state,
                failedMemberIds: action.payload,
            };
        }
        case DRAW_EDIT_GET_DRAW_DIVISION_ASSOCIATION_SUCCESS: {
            const { drawDivision } = action.payload;
            const associatedDivisionIds = [];
            const associatedDivisionsByDrawId = {};
            Object.entries(drawDivision).forEach(([drawId, divisionIds]) => {
                divisionIds.forEach(divId => associatedDivisionIds.push(divId));
                associatedDivisionsByDrawId[drawId] = divisionIds;
            });

            return {
                ...state,
                drawDivision,
                associatedDivisionIds,
                associatedDivisionsByDrawId,
            };
        }
        case DRAW_EDIT_UPDATE_CHECK_IN_STATUS_SUCCESS: {
            const { memberId, checkIns } = action.payload;
            const previousPlayers = cloneDeep(state.players.data);
            const previousPlayersDictByMemberId = cloneDeep(state.players.playersDictByMemberId);

            const player = previousPlayers.find((p) => p.memberId === memberId);
            if (player) {
                let newCheckInDraws = cloneDeep(player.checkInDraws);
                newCheckInDraws = newCheckInDraws.map((checkInDraw) => {
                    const newCheckIn = checkIns.find((checkIn) => {
                        return checkIn.drawId === checkInDraw.drawIdUsedForCheckIn;
                    });

                    if (newCheckIn) {
                        return {
                            ...checkInDraw,
                            checkedIn: newCheckIn.checkedIn,
                        };
                    }

                    return checkInDraw;
                });
                player.checkInDraws = newCheckInDraws;
                previousPlayersDictByMemberId[memberId].checkInDraws = newCheckInDraws;
            }

            return {
                ...state,
                players: {
                    ...state.players,
                    data: previousPlayers,
                    playersDictByMemberId: previousPlayersDictByMemberId,
                },
            };
        }
        case DRAW_EDIT_CONFIRM_DIVISION_DRAW_ASSOCIATION_SUCCESS: {
            return {
                ...state,
                event: {
                    ...state.event,
                    data: {
                        ...state.event.data,
                        confirmDivisionDrawAssociation: true,
                    },
                },
            };
        }
        case DRAW_EDIT_GET_DRAWS_PUBLISH_INFO_SUCCESS: {
            const drawsForPublishData = action.payload;
            return {
                ...state,
                drawsForPublish: drawsForPublishData,
            };
        }
        case DRAW_EDIT_TOGGLE_SMS_FLAG: {
            const isSMSEnabled = action.payload;
            return {
                ...state,
                event: {
                    ...state.event,
                    data: {
                        ...state.event.data,
                        smsEnabled: isSMSEnabled,
                    },
                },
            };
        }
        default:
            return state;
    }
};
